// imports
import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Typography } from '@mui/material';

// local imports
import CustomInput from '../../../CustomInput';
import ColorPickerPopover from '../../../ColorPickerPopover';
import {
  defaultLineColor,
  GeoJsonAdditionalLayer,
} from '../../../../../configs/additionalLayers';
import { mapActions } from '../../../../../store';

interface LineLayerControlsProps {
  layer: GeoJsonAdditionalLayer;
}

const minLineWidth = 1;
const maxLineWidth = 99;

/**
 * LineLayerControls
 *
 * Renders the line color and line width controls for the vector data with geometryType 'line'
 *
 * @param layer - The additionalLayer object
 * @returns {JSX.Element} The rendered line color and width controls.
 */
export default function LineLayerControls({ layer }: LineLayerControlsProps) {
  const dispatch = useDispatch();
  const [localLineColor, setLocalLineColor] = useState<string>(
    layer.lineColor || defaultLineColor
  );

  useEffect(() => {
    dispatch(
      mapActions.setLineColor({
        id: layer.id,
        lineColor: localLineColor,
      })
    );
  }, [localLineColor, layer.id, dispatch]);

  const handleLineWidthChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newValue = event.target.value;
    if (newValue === '') {
      dispatch(mapActions.setLineWidth({ id: layer.id, lineWidth: '' }));
      return;
    }
    const numericValue = Number(newValue);
    if (
      !isNaN(numericValue) &&
      numericValue >= minLineWidth &&
      numericValue <= maxLineWidth
    ) {
      dispatch(
        mapActions.setLineWidth({ id: layer.id, lineWidth: numericValue })
      );
    }
  };

  return (
    <Box id="line-container" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography component="span" variant="caption" sx={{ fontWeight: 'bold', textTransform: 'uppercase', color: '#555', letterSpacing: '0.05em' }}>
        Line
      </Typography>
      <Box sx={{ display: 'flex', width: '100%', gap: 1, alignItems: 'center' }}>
        <ColorPickerPopover
        color={localLineColor}
        onChange={setLocalLineColor}
        data-testid="line-color-picker"
        aria-label="Line color"
      />
      <CustomInput
        data-testid="line-width-picker"
        value={layer.lineWidth || ''}
        onChange={handleLineWidthChange}
        width="80px"
        suffix="px"
        type="number"
        min={minLineWidth.toString()}
        max={maxLineWidth.toString()}
        aria-label="Line width in pixels"
      />
      </Box>
    </Box>
  );
}
